import { useEffect, type ReactNode } from "react";

import { toastApiError } from "@/shared/utils/toast";

import { useAccessApiKeys } from "../hooks/useAccessApiKeys";
import { useAccessUsers } from "../hooks/useAccessUsers";
import { usePolicies } from "../hooks/usePolicies";
import { useRoles } from "../hooks/useRoles";
import { ApiKeysContext, PoliciesContext, RolesContext, UsersContext } from "./AccessDataContext";

/**
 * Single source of truth for the "Access & permissions" sections (Usuarios, API Keys, Roles, Políticas,
 * Simulador). Those sections all render at once inside one scrolling `SettingsPage`, so fetching roles /
 * policies / users / API keys per section meant duplicate network calls AND independent caches — a policy
 * created in the Políticas section never reached the Crear-rol drawer until a full reload.
 *
 * This provider instantiates the existing data hooks **once** and exposes each entity through its **own**
 * context. Splitting the contexts (instead of one big value) keeps re-renders surgical: a change to policies
 * only re-renders policy consumers, not the user/API-key tables. Each hook already memoizes its own return,
 * so the provider forwards those values directly — adding a field to a hook needs no change here. Mutations
 * call the matching `load()` to invalidate, so every section stays in sync. No Redux, no new dependency —
 * module-local shared state, the same hook + service pattern the rest of settings uses.
 */
export function AccessDataProvider({ children }: { children: ReactNode }) {
  const rolesData = useRoles();
  const policiesData = usePolicies();
  const usersData = useAccessUsers();
  const apiKeysData = useAccessApiKeys();

  // Destructure the (stable, useCallback) loaders so the mount effect depends on them — not on the whole data
  // objects, which change on every fetch and would re-trigger an infinite reload.
  const { load: loadRoles } = rolesData;
  const { load: loadPolicies } = policiesData;
  const { load: loadKeys } = apiKeysData;

  // One initial load for the whole module. The hooks set `hasNoAccess` (no throw) on a 403, so the toast only
  // fires for genuine errors — same UX the sections had individually.
  //
  // Users is deliberately absent: `useAccessUsers` owns its own filter/pagination effect and issues the
  // first page from there. Loading it here as well would fire two requests on every mount.
  useEffect(() => {
    void loadRoles().catch(toastApiError);
    void loadPolicies().catch(toastApiError);
    void loadKeys().catch(toastApiError);
  }, [loadRoles, loadPolicies, loadKeys]);

  return (
    <RolesContext.Provider value={rolesData}>
      <PoliciesContext.Provider value={policiesData}>
        <UsersContext.Provider value={usersData}>
          <ApiKeysContext.Provider value={apiKeysData}>{children}</ApiKeysContext.Provider>
        </UsersContext.Provider>
      </PoliciesContext.Provider>
    </RolesContext.Provider>
  );
}
